import type { RefObject } from 'react'
import { ListRow } from '../../platform/primitives/ListRow'
import { PersonAvatar } from '../../platform/primitives/PersonAvatar'
import { MemberActionsMenu } from './MemberActionsMenu'
import { RoleMenu } from './RoleMenu'
import { canChangeRole, canManageMembers, isSelf, memberLabel, ROLE_LABELS, type Role } from './roles'
import type { Member } from './useMembers'
import './household.css'

/** en-GB, matching `Async`'s own stale-date formatting — the one other place
 *  this app renders a bare date today. */
const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

/**
 * One member. Task 4 shipped this render-only, with no role menu, no remove,
 * no leave. Task 5 (below) adds the role control; Tasks 6 and 7 add the
 * remaining two.
 *
 * `label` is the display name, falling back to the email when it is null
 * (`user_display_name` is nullable on `MembershipResponse`) — the same
 * fallback `PersonAvatar`'s own initials use. `sublabel` carries the OTHER
 * identifier: the email, but only when the label is already the name — when
 * the label had to fall back to the email itself, repeating it in `sublabel`
 * would show the same string twice.
 *
 * **The joined date sits in `trailing`, beside the role, not inside
 * `sublabel`.** `ListRow`'s own `sublabel` takes one plain string rendered as
 * a single text node; concatenating the email and the date into one sentence
 * would make the email unfindable as its own text, which the household screen
 * has to be able to do.
 *
 * **A member whose role does not narrow renders `rawRole` verbatim** — issue
 * 306's client-side half. Not "Unknown", not blank: the server said something
 * real and this client has not been taught it yet, and hiding that would read
 * as a rendering bug rather than an unrecognised value.
 *
 * **Task 5 adds the role control**; `members`, `myUserId` and `myRole` decide
 * whether `RoleMenu` replaces the plain role text, and `onChangeRole` is what
 * it calls. All six props are optional, defaulted to the shape Task 4's own
 * render used (no roster, no viewer, `myRole: null`), so every earlier test
 * still renders exactly as before: `canChangeRole(null)` is `false`, so the
 * row falls straight to the plain-text branch.
 *
 * **`onChangeRole` missing is also a reason to fall back to text**, matching
 * `InvitationRow`'s own rule for `onResend`/`onRevoke` (Task 8's docblock):
 * a control with nowhere to send its result is worse than no control, so the
 * three permission guards below are joined by a fourth, `onChangeRole !==
 * undefined`, before `RoleMenu` is ever mounted.
 *
 * **Task 6 adds `MemberActionsMenu`** — the row's own "More actions" trigger,
 * beside `RoleMenu` in the SAME trailing slot, rendered whenever the viewer
 * `canManageMembers` or the row is their own (`MemberActionsMenu` itself
 * decides Remove vs. Leave from `isSelf`, and renders nothing when neither
 * `onRemove` nor `onLeave` applies — same "no callback, no control" rule).
 * `onLeave` is Task 7's own wiring (tracke5-webapp#314) — `Household.tsx`
 * passes the same handler its footer "Leave household" control uses, so the
 * row's own Leave item and the footer open the identical confirmation.
 */
type MemberRowProps = {
  member: Member
  members?: readonly Member[]
  myUserId?: string
  myRole?: Role | null
  /**
   * `Household.tsx` passes `useChangeRole`'s own `change` — a promise, so a
   * failed PATCH can be caught and shown rather than vanishing (`RoleMenu`
   * does the catching, per row, matching `InvitationRow`'s `resending`).
   */
  onChangeRole?: (membershipId: string, role: Role) => Promise<void>
  /**
   * `Household.tsx` passes `useRemoveMember`'s own `remove` — a promise, so
   * `ConfirmDialog`'s own `onConfirm` contract (await, stay open and show the
   * reason on rejection) has something to await.
   */
  onRemove?: (membershipId: string) => Promise<void>
  /**
   * `Household.tsx` passes `() => setLeaving(true)` — the same handler its
   * footer control uses. `undefined` is still meaningful: every test in
   * this file that omits it renders no Leave item, matching
   * `MemberActionsMenu`'s "no callback, no control" rule.
   */
  onLeave?: () => void
  /**
   * Threaded straight through to `MemberActionsMenu` — see ITS docblock for
   * the defect this closes (web-architect review, issue 313, blocking
   * finding B1). `Household.tsx` passes its own `inviteButton`, an anchor
   * that survives a member's row unmounting after a confirmed removal.
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
}

export function MemberRow({
  member,
  members = [],
  myUserId,
  myRole = null,
  onChangeRole,
  onRemove,
  onLeave,
  restoreFocusTo,
}: MemberRowProps) {
  const label = memberLabel(member)
  const sublabel = member.displayName ? (member.email ?? undefined) : undefined
  const roleLabel = member.role ? ROLE_LABELS[member.role] : member.rawRole

  /**
   * Four guards, all before the control is even offered — CLAUDE.md's own
   * bar for a permission decision that must fail closed:
   *
   *  - `member.role !== null` — issue 306. An unrecognised role grants
   *    nothing to anyone, including the owner viewing this row.
   *  - `canChangeRole(myRole)` — **not** `canManageMembers`. Owner only
   *    (`household_service.py:363`); an admin passing `canManageMembers`
   *    here would get a control the server 403s.
   *  - `!isSelf(...)` — `household_service.py:374`, "Cannot change your own
   *    role". Nobody, not even the last owner, may act on their own row.
   *  - `onChangeRole !== undefined` — a control with no mutation to call is
   *    worse than no control (`InvitationRow`'s own rule for its two).
   *
   * `isLastOwner` is deliberately NOT one of these four: it is a per-OPTION
   * guard, not a per-row one, so `RoleMenu` — which sees every option —
   * enforces it, disabling the ones that would leave the household without
   * an owner rather than hiding the whole control.
   */
  const canControl =
    member.role !== null &&
    canChangeRole(myRole) &&
    !isSelf(member.userId, myUserId) &&
    onChangeRole !== undefined

  /**
   * Task 6's own gate for `MemberActionsMenu` — "or the row is the signed-in
   * user's own", per the plan's Step 4. Unlike `canControl`, `canManageMembers`
   * IS the right predicate here (not `canChangeRole`): remove is
   * `owner|admin` (`household_service.py:424`), and Leave needs no permission
   * check at all — every member may leave their own household, which is
   * exactly why `isSelf` is checked with `||` rather than folded into a
   * fourth `&&`. `MemberActionsMenu` itself decides which of Remove/Leave
   * that resolves to, and renders neither trigger nor panel when it resolves
   * to nothing offering.
   */
  const self = isSelf(member.userId, myUserId)
  const canOpenActions = canManageMembers(myRole) || self

  return (
    <ListRow
      label={label}
      sublabel={sublabel}
      leading={<PersonAvatar name={member.displayName} email={member.email} />}
      trailing={
        <span className="member-row__meta">
          <span className="member-row__controls">
            {canControl && member.role !== null && onChangeRole !== undefined ? (
              <RoleMenu
                role={member.role}
                rawRole={member.rawRole}
                label={label}
                members={members}
                onChangeRole={(role) => onChangeRole(member.id, role)}
              />
            ) : (
              <span className="member-row__role">{roleLabel}</span>
            )}
            {canOpenActions && (
              <MemberActionsMenu
                membershipId={member.id}
                rawRole={member.rawRole}
                label={label}
                members={members}
                isSelf={self}
                onRemove={onRemove}
                onLeave={onLeave}
                restoreFocusTo={restoreFocusTo}
              />
            )}
          </span>
          <span className="member-row__joined">
            {`Joined ${DATE.format(new Date(member.joinedAt))}`}
          </span>
        </span>
      }
    />
  )
}
